import { Controller } from "@hotwired/stimulus"
import { usesSidebarLayout } from "../utils/layout_media"
import { scrollThreadToEnd } from "../utils/thread_scroll"
import { JANE_SIDEBAR_OPEN_KEY } from "./jane_sidebar_controller"
const URL_PARAM = "open"

// Desktop right sidebar for the unified Messages list (mirrors the Network
// contact detail sidebar). Rows carrying data-detail-url open their full
// email/chat detail — fetched from the server, same content as the standalone
// /emails/:id and /messages/chats/:id pages — in a .detail-sidebar appended
// to .assistant-phone. On mobile/touch the click falls through to the row's
// normal link navigation. The open panel is tracked in a ?open=email-123 /
// ?open=chat-45 query param so a refresh restores it. defaultOpenUrl is the
// assistant detail panel, used to open an `assistant-<id>` conversation row;
// pages that also render shared/_jane_sidebar leave defaultOpenKey unset, so
// Jane herself is that sidebar's job and never a second panel here.
export default class extends Controller {
    static values = {
        defaultOpenUrl: String,
        defaultOpenKey: String,
        // The copy in the layout (application.html.slim): no list of its own,
        // it opens a conversation for a "Message" button anywhere
        // ("jane:open-conversation") and never touches the URL or Jane.
        host: { type: Boolean, default: false }
    }

    connect() {
        this._onClick = (e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
            // Inline /contacts/:id links (Jane's digest messages) open the
            // same contact detail sidebar as Network rows instead of
            // navigating away; on mobile they fall through to the page.
            let link = e.target.closest("a[href]")
            if (link && this.element.contains(link) && this._useSidebar()) {
                let contactId = (link.getAttribute("href") || "").match(/^\/contacts\/(\d+)(?:[?#]|$)/)?.[1]
                if (contactId) {
                    e.preventDefault()
                    e.stopPropagation()
                    if (this._inPanel()) this._openFromPanel(`/network/contacts/${contactId}/detail`, `contact-${contactId}`, link)
                    else this._openPanel(`/network/contacts/${contactId}/detail`, `contact-${contactId}`, null)
                    return
                }
            }
            let row = e.target.closest("[data-detail-url]")
            if (!row || !this.element.contains(row)) return
            if (!this._useSidebar()) return
            e.preventDefault()
            e.stopPropagation()
            if (this._inPanel()) this._openFromPanel(row.dataset.detailUrl, row.dataset.detailKey || row.dataset.detailUrl, row)
            else this._openRow(row)
        }
        this._onResize = () => {
            if (this._sidebarEl && !this._useSidebar()) this._close()
        }
        this._onKeydown = (e) => {
            if (e.key === "Escape") this._close({ persist: true })
        }
        this._onBeforeCache = () => this._close()
        this._onToggle = () => this._toggleJane()
        // A turbo_stream can replace the panel's content under us — saving the
        // contact edit form swaps the whole detail card in place. The kebab is
        // hoisted OUT of that card into the actions bar, so the replacement
        // arrives carrying a second one; re-run the hoist to drop the stale
        // copy. Streams that touch nothing in the panel are left alone.
        this._onStreamRender = (event) => {
            let render = event.detail.render
            event.detail.render = (stream) => {
                render(stream)
                let content = this._sidebarEl?.querySelector(".detail-sidebar-content")
                if (content?.querySelector(".msg-detail-menu, .msg-detail-archive")) this._mountActionsMenu(content)
            }
        }
        // One right sidebar at a time: another panel opening asks this one
        // to step aside.
        this._onCloseOthers = (event) => {
            if (event.detail?.except !== this && this._sidebarEl) this._close({ restoreJane: false })
        }
        this.element.addEventListener("click", this._onClick)
        window.addEventListener("resize", this._onResize)
        document.addEventListener("turbo:before-cache", this._onBeforeCache)
        document.addEventListener("turbo:before-stream-render", this._onStreamRender)
        document.addEventListener("jane:detail-sidebars-close", this._onCloseOthers)
        if (this.hostValue) {
            this._onOpenConversation = (event) => this._openConversation(event)
            window.addEventListener("jane:open-conversation", this._onOpenConversation)
            return
        }
        document.addEventListener("jane-sidebar:toggle", this._onToggle)
        this._restoreFromUrl()
    }

    // A person's "Message" (contact_message_controller): their conversation in
    // this sidebar. Whatever sidebar was open steps aside - on /network that is
    // their card, and Back (in the actions bar) brings it back.
    //
    // The same event carries a panel the host shows itself - the header bell's
    // round-up (round_up_trigger_controller) - and anything opened from inside
    // one (backUrl/backKey): Back then reopens that panel. Asking again for
    // the panel already showing closes it, so the bell toggles.
    _openConversation(event) {
        let detail = event.detail || {}
        if (!detail.url || !this._useSidebar() || !document.querySelector(".assistant-phone")) return
        detail.handled = true
        if (this._sidebarEl && detail.key && detail.key === this._activeKey) {
            this._close()
            return
        }
        this._backPanel = detail.backUrl ? { url: detail.backUrl, key: detail.backKey } : null
        this._backTrigger = this._backPanel ? null : document.querySelector("[data-detail-url].is-selected")
        document.dispatchEvent(new CustomEvent("jane:detail-sidebars-close", { detail: { except: this } }))
        this._openPanel(detail.url, detail.key, null)
        this._mountBack()
    }

    // A list inside a panel (the round-up's email rows, its cards): the host
    // opens the row in the same sidebar, with Back to the panel, instead of
    // this controller stacking a second sidebar on top. Without a host to
    // take it (no split layout shell) the row's own link is followed.
    _inPanel() {
        return !this.hostValue && !!this.element.closest(".detail-sidebar [data-panel-url]")
    }

    _openFromPanel(url, key, link) {
        let panel = this.element.closest("[data-panel-url]")
        let detail = { url, key, backUrl: panel.dataset.panelUrl, backKey: panel.dataset.panelKey }
        window.dispatchEvent(new CustomEvent("jane:open-conversation", { detail }))
        let href = link?.getAttribute("href")
        if (!detail.handled && href) window.Turbo ? window.Turbo.visit(href) : (window.location.href = href)
    }

    _mountBack() {
        let actions = this._sidebarEl?.querySelector(".detail-sidebar-actions")
        actions?.querySelector(".detail-sidebar-back")?.remove()
        let panel = this._backPanel
        if (actions && panel) {
            actions.prepend(this._backButton(() => {
                this._backPanel = null
                this._openPanel(panel.url, panel.key, null)
                this._mountBack()
            }))
            return
        }
        let trigger = this._backTrigger
        if (!actions || !trigger) return

        actions.prepend(this._backButton(() => {
            this._backTrigger = null
            this._close({ restoreJane: false })
            if (trigger.isConnected) trigger.click()
        }))
    }

    _backButton(onClick) {
        let back = document.createElement("button")
        back.type = "button"
        back.className = "detail-sidebar-back"
        back.setAttribute("aria-label", "Back")
        back.title = "Back"
        back.innerHTML = '<i class="bi bi-arrow-left"></i>'
        back.addEventListener("click", onClick)
        return back
    }

    disconnect() {
        this.element.removeEventListener("click", this._onClick)
        window.removeEventListener("resize", this._onResize)
        document.removeEventListener("turbo:before-cache", this._onBeforeCache)
        document.removeEventListener("jane-sidebar:toggle", this._onToggle)
        document.removeEventListener("turbo:before-stream-render", this._onStreamRender)
        document.removeEventListener("jane:detail-sidebars-close", this._onCloseOthers)
        if (this._onOpenConversation) window.removeEventListener("jane:open-conversation", this._onOpenConversation)
        this._teardownSidebar()
    }

    _useSidebar() {
        return usesSidebarLayout()
    }

    _restoreFromUrl() {
        if (!this._useSidebar()) return
        let key = new URL(window.location.href).searchParams.get(URL_PARAM)
        if (!key) {
            this._openDefaultIfNeeded()
            return
        }
        let row = this.element.querySelector(`[data-detail-key="${CSS.escape(key)}"]`)
        if (row) {
            this._openRow(row)
            row.scrollIntoView({ block: "nearest" })
            return
        }
        let contactId = key.match(/^contact-(\d+)$/)?.[1]
        if (contactId) {
            this._openPanel(`/network/contacts/${contactId}/detail`, key, null)
            return
        }
        if (this.hasDefaultOpenKeyValue && key === this.defaultOpenKeyValue && this.hasDefaultOpenUrlValue) {
            this._openPanel(this.defaultOpenUrlValue, key, null)
            return
        }
        if (key.startsWith("assistant-") && this.hasDefaultOpenUrlValue) {
            let id = key.slice("assistant-".length)
            if (id) {
                let url = `${this.defaultOpenUrlValue}?conversation_id=${encodeURIComponent(id)}`
                this._openPanel(url, key, null)
            }
        }
    }

    _openDefaultIfNeeded() {
        if (!this.hasDefaultOpenUrlValue || !this.defaultOpenUrlValue) return
        if (!this.hasDefaultOpenKeyValue || !this.defaultOpenKeyValue) return
        // Respect the saved open/closed preference (shared with the Jane sidebar
        // on other pages) instead of force-opening every visit.
        if (!this._prefersJaneOpen()) {
            this._syncToggleButtons(false)
            return
        }
        this._openPanel(this.defaultOpenUrlValue, this.defaultOpenKeyValue, null)
    }

    // Header sparkles button (jane-sidebar:toggle) opens/closes the Jane starter
    // panel and persists the choice, mirroring the Jane sidebar on other pages.
    _toggleJane() {
        if (!this._useSidebar()) return
        if (this._sidebarEl && this._isJaneActive) {
            this._close({ persist: true })
            return
        }
        // A row detail is open, and it took the Jane sidebar's place (see
        // _hideJaneSidebar). Close it WITHOUT restoring — the jane-sidebar
        // controller is handling this same toggle and is about to show the
        // panel itself, so restoring here would just race it.
        if (this._sidebarEl) this._close({ restoreJane: false })
        if (!this.hasDefaultOpenUrlValue || !this.defaultOpenUrlValue) return
        if (!this.hasDefaultOpenKeyValue || !this.defaultOpenKeyValue) return
        this._persistJaneOpen(true)
        this._openPanel(this.defaultOpenUrlValue, this.defaultOpenKeyValue, null)
    }

    _prefersJaneOpen() {
        try {
            return localStorage.getItem(JANE_SIDEBAR_OPEN_KEY) !== "false"
        } catch {
            return true
        }
    }

    _persistJaneOpen(open) {
        try {
            localStorage.setItem(JANE_SIDEBAR_OPEN_KEY, open ? "true" : "false")
        } catch {
            // ignore storage errors
        }
    }

    _syncToggleButtons(open) {
        if (this.hostValue) return
        document.querySelectorAll(".jane-sidebar-toggle").forEach((btn) => {
            btn.classList.toggle("is-active", open)
            btn.setAttribute("aria-pressed", open ? "true" : "false")
        })
    }

    _openRow(row) {
        if (this._sidebarEl && this._activeRow === row) {
            this._close()
            return
        }
        // Opening marks the thread read server-side; clear the dot right away.
        row.querySelectorAll(".msg-row-unread-dot").forEach((dot) => dot.remove())
        this._openPanel(row.dataset.detailUrl, row.dataset.detailKey, row)
    }

    _openPanel(url, key, row) {
        let phone = document.querySelector(".assistant-phone")
        if (!phone || !url) return

        this._hideJaneSidebar()

        if (!this._sidebarEl) {
            let sidebar = document.createElement("aside")
            sidebar.className = "detail-sidebar detail-sidebar--message"
            sidebar.setAttribute("role", "complementary")
            sidebar.setAttribute("aria-label", "Message details")
            sidebar.innerHTML = `
                <div class="detail-sidebar-actions">
                    <button type="button" class="detail-sidebar-close assistant-header-btn assistant-filter-toggle assistant-avatar" aria-label="Close">
                        <i class="bi bi-x-lg"></i>
                    </button>
                </div>
                <div class="detail-sidebar-content"></div>`
            sidebar.querySelector(".detail-sidebar-close").addEventListener("click", () => this._close({ persist: true }))
            phone.appendChild(sidebar)
            phone.classList.add("is-detail-open")
            this._sidebarEl = sidebar
            document.addEventListener("keydown", this._onKeydown)
        }

        if (this._activeRow) this._activeRow.classList.remove("is-selected")
        this._activeRow = row
        if (row) row.classList.add("is-selected")
        this._activeKey = key
        this._isJaneActive = this.hasDefaultOpenKeyValue && key === this.defaultOpenKeyValue
        this._syncToggleButtons(this._isJaneActive)
        this._setUrlParam(key)
        this._load(url, row)
    }

    _load(url, row) {
        this._unmountActionsMenu()
        this._stopThreadPin?.()
        let content = this._sidebarEl.querySelector(".detail-sidebar-content")
        content.innerHTML = this._skeleton()

        let token = (this._loadToken = (this._loadToken || 0) + 1)
        fetch(url, {
            headers: { "Accept": "text/html" },
            credentials: "same-origin"
        })
            .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then((html) => {
                if (token !== this._loadToken || !this._sidebarEl) return
                content.innerHTML = html
                this._mountActionsMenu(content)
                // Threads render oldest-first: open on the newest message, not
                // on whatever the conversation started with months ago.
                this._stopThreadPin = scrollThreadToEnd(content)
            })
            .catch(() => {
                if (token !== this._loadToken || !this._sidebarEl) return
                let fallback = row?.getAttribute("href")
                content.innerHTML = `
                    <div class="msg-detail-loading msg-detail-loading--error">
                        <span>Could not load this message.</span>
                        ${fallback ? `<a href="${fallback}" target="_blank" rel="noopener">Open in new window</a>` : ""}
                    </div>`
            })
    }

    // Drawn the instant a panel opens, shaped like what is coming, so the
    // sidebar answers the click while the server composes it. The bell's
    // round-up is mostly cards; anything else (a thread, a contact) is a
    // heading over lines of text.
    _skeleton() {
        let cards = this._activeKey === "round-up"
        let line = (width) => `<div class="msg-detail-skeleton-line" style="width: ${width}%"></div>`
        let body = cards
            ? [0, 1, 2].map(() => `<div class="msg-detail-skeleton-card">${line(45)}${line(90)}${line(70)}</div>`).join("")
            : [92, 85, 96, 60, 88, 74].map(line).join("")
        return `
            <div class="msg-detail-skeleton" role="status" aria-label="Loading">
                <div class="msg-detail-skeleton-title">${cards ? "Round-up" : ""}</div>
                ${cards ? line(65) : ""}
                ${body}
            </div>`
    }

    _mountActionsMenu(content) {
        if (!this._sidebarEl) return
        let actions = this._sidebarEl.querySelector(".detail-sidebar-actions")
        let closeBtn = actions?.querySelector(".detail-sidebar-close")
        if (!actions || !closeBtn) return
        this._unmountActionsMenu()
        // Hoist the kebab (⋮) then the inline archive button so the persistent
        // actions bar reads ⋮ · Archive · ✕. Archive is only present for detail
        // types that support it (email), so the button is otherwise absent.
        let menu = content.querySelector(".msg-detail-menu")
        if (menu) actions.insertBefore(menu, closeBtn)
        let archive = content.querySelector(".msg-detail-archive")
        if (archive) actions.insertBefore(archive, closeBtn)
    }

    // The kebab and inline archive live in the panel HTML but get moved into
    // the persistent actions bar, so they survive content swaps — remove any
    // stale ones first.
    _unmountActionsMenu() {
        this._sidebarEl?.querySelectorAll(".detail-sidebar-actions .msg-detail-menu, .detail-sidebar-actions .msg-detail-archive")
            .forEach((el) => el.remove())
    }

    close() {
        this._close({ persist: true })
    }

    // Called by msg-detail-actions after a triage action (archive / trash /
    // spam / move-to-inbox) takes the open thread out of the list: keep the
    // panel up and slide it onto the next thread so the next action is one
    // click away. Falls back to the previous row at the end of the list, and
    // closes only when nothing is left to triage.
    advanceAfterRemoval(removedRow) {
        if (!this._sidebarEl) return
        let next = this._neighborRow(removedRow || this._activeRow)
        if (!next) {
            this._close()
            return
        }
        // Not _openRow: that toggles the panel shut when handed the row that
        // is already active, and opening always marks the thread read, so the
        // dot goes now.
        next.querySelectorAll(".msg-row-unread-dot").forEach((dot) => dot.remove())
        this._openPanel(next.dataset.detailUrl, next.dataset.detailKey, next)
        next.scrollIntoView({ block: "nearest" })
    }

    // Nearest still-triageable row to `from` — forward first, then backward.
    // Rows mid-collapse from an earlier action are skipped: they are still in
    // the DOM for another ~240ms but are already gone as far as the user is
    // concerned.
    _neighborRow(from) {
        if (!from) return null
        let rows = Array.from(this.element.querySelectorAll("[data-detail-url]"))
        let index = rows.indexOf(from)
        if (index === -1) return null
        let usable = (row) => {
            if (row === from) return false
            let unit = row.closest(".msg-swipe") || row
            return !unit.classList.contains("is-leaving") && !unit.classList.contains("is-collapsed")
        }
        for (let i = index + 1; i < rows.length; i++) if (usable(rows[i])) return rows[i]
        for (let i = index - 1; i >= 0; i--) if (usable(rows[i])) return rows[i]
        return null
    }

    _close({ persist = false, restoreJane = true } = {}) {
        // Only closing the Jane starter panel writes the shared preference;
        // dismissing a transient email/chat detail leaves it untouched.
        if (persist && this._isJaneActive) this._persistJaneOpen(false)
        this._isJaneActive = false
        this._syncToggleButtons(false)
        this._setUrlParam(null)
        this._teardownSidebar()
        if (restoreJane) this._restoreJaneSidebar()
    }

    // Messages renders the real Jane sidebar (shared/_jane_sidebar) as its own
    // pane in this same flex row, so a detail panel opened beside it squeezed
    // the list between two 400px chat panes. Detail takes the sidebar's place
    // while it is up and hands it back on close — the same trade the Network
    // and Events detail panes make (detail_modal / event_detail).
    _hideJaneSidebar() {
        let jane = document.querySelector(".detail-sidebar--jane")
        if (!jane) return
        this.application.getControllerForElementAndIdentifier(jane, "jane-sidebar")?.hideForDetail()
    }

    _restoreJaneSidebar() {
        document.dispatchEvent(new CustomEvent("jane-sidebar:restore"))
    }

    _teardownSidebar() {
        this._stopThreadPin?.()
        this._stopThreadPin = null
        if (this._activeRow) {
            this._activeRow.classList.remove("is-selected")
            this._activeRow = null
        }
        this._activeKey = null
        this._backPanel = null
        // Only a panel this controller opened is ours to close: a frame visit's
        // turbo:before-cache lands here too, and the open Jane panel (whose
        // is-detail-open this is then) must survive it.
        if (this._sidebarEl) {
            this._sidebarEl.remove()
            this._sidebarEl = null
            document.removeEventListener("keydown", this._onKeydown)
            document.querySelector(".assistant-phone")?.classList.remove("is-detail-open")
        }
    }

    _setUrlParam(key) {
        if (this.hostValue) return
        let url = new URL(window.location.href)
        if (key) {
            url.searchParams.set(URL_PARAM, key)
        } else if (url.searchParams.has(URL_PARAM)) {
            url.searchParams.delete(URL_PARAM)
        } else {
            return
        }
        window.history.replaceState({}, "", url)
    }
}
